import React from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { CheckCircle, MessageCircle, Home, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatCOP, buildWhatsAppUrl } from '@/utils/format';

const OrderConfirmation = () => {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { order, payment } = location.state || {};

  const isBoldConfigured = payment?.redirect_url && !payment.status?.includes('pending_configuration');

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
      <div className="text-center mb-10">
        <div className="w-20 h-20 rounded-full bg-[#2A5C43]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-10 h-10 text-[#2A5C43]" />
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight mb-2">
          ¡Gracias por tu compra!
        </h1>
        <p className="text-[#57534E]">
          Tu pedido <span className="font-semibold text-[#2A5C43]" data-testid="order-number">{orderNumber}</span> ha sido recibido
        </p>
      </div>

      {!isBoldConfigured && (
        <div className="bg-[#F5E6D3] border border-[#D47A4A]/30 rounded-2xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <Package className="w-5 h-5 text-[#D47A4A] shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#6B5D3F] mb-1">Pago pendiente de configuración</p>
              <p className="text-sm text-[#6B5D3F]">
                La pasarela de pagos Bold aún no está configurada. Nuestro equipo se pondrá en contacto contigo para completar el pago de forma manual mediante consignación, transferencia o WhatsApp.
              </p>
            </div>
          </div>
        </div>
      )}

      {order && (
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-6 mb-6">
          <h2 className="font-display font-semibold text-lg text-[#1C1917] mb-4">Resumen del pedido</h2>
          <div className="space-y-3 mb-4">
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <div>
                  <p className="font-medium text-[#1C1917]">{item.product_name}</p>
                  <p className="text-xs text-[#57534E]">{item.size} × {item.quantity}</p>
                </div>
                <p className="font-semibold text-[#2A5C43]">{formatCOP(item.subtotal)}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-[#E7E5E4] pt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#57534E]">Subtotal</span>
              <span className="text-[#1C1917]">{formatCOP(order.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#57534E]">Envío</span>
              <span className="text-[#1C1917]">{formatCOP(order.shipping_cost)}</span>
            </div>
            <div className="border-t border-[#E7E5E4] pt-2 flex justify-between items-baseline">
              <span className="font-display font-semibold text-[#1C1917]">Total</span>
              <span className="font-display font-bold text-2xl text-[#2A5C43]">{formatCOP(order.total)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6">
          <Link to="/" data-testid="conf-home-btn">
            <Home className="w-4 h-4 mr-2" /> Volver al inicio
          </Link>
        </Button>
        <a
          href={buildWhatsAppUrl(`Hola, tengo el pedido ${orderNumber} y quisiera coordinar el pago`)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-6 py-2 rounded-full border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white font-semibold text-sm"
          data-testid="conf-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4" /> Contactar por WhatsApp
        </a>
      </div>
    </div>
  );
};

export default OrderConfirmation;
